/**
 * @file index.tsx
 *
 * @fileOverview Renders job tracking pixel.
 */

import React, { useMemo, memo } from 'react';
import { useClientScriptInsert } from '~/jobiqo/hooks/useClientScriptInsert';
import { getProcessedTrackingPixelCode } from '~/jobiqo/components/Job/Detail/utils';
import { useRouter } from 'next/router';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

/**
 * Props for the TrackingPixel component.
 *
 * @property {string} trackingPixelCode
 *   The raw tracking pixel code to be processed.
 * @property {string} jobId
 *   The unique ID of the job for tracking purposes.
 * @property {string} identifier
 *   The unique identifier for this tracking pixel, used for
 *   distinguishing between different pixel scripts.
 */
interface TrackingPixelProps {
  trackingPixelCode: string;
  jobId: string;
  identifier: string;
}

export const TrackingPixel = memo(
  ({ trackingPixelCode, jobId, identifier }: TrackingPixelProps) => {
    const router = useRouter();
    const { JOBS } = useFrontendConfig();
    const searchParams = new URLSearchParams(router.query as any);

    const processedPixelCode = useMemo(() => {
      return (
        getProcessedTrackingPixelCode(
          trackingPixelCode,
          searchParams,
          JOBS.JOB_TRACKING_PIXEL_WITH_REQUIRED_FROM_QUERY_PARAMS,
        ) ?? ''
      );
    }, [
      JOBS.JOB_TRACKING_PIXEL_WITH_REQUIRED_FROM_QUERY_PARAMS,
      trackingPixelCode,
      searchParams,
    ]);

    const { strippedJsString } = useClientScriptInsert({
      content: processedPixelCode,
      id: jobId,
      identifier,
    });

    return strippedJsString ? (
      <div
        key={identifier}
        style={{ height: 0 }}
        dangerouslySetInnerHTML={{
          __html: strippedJsString,
        }}
      />
    ) : null;
  },
);
